import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Globe, Plus, Check, Power, PowerOff, Trash2,
  Loader2, AlertCircle, CheckCircle, ExternalLink, Users, Code
} from 'lucide-react';

const SNIPPET_TEMPLATE = (snippetKey) => `<script>
(function(w,d){'use strict';var C={siteKey:'${snippetKey}',endpoint:'https://otherwise.app.n8n.cloud/webhook/web-visit',cookieName:'_cg_id',cookieDays:90,sessionKey:'_cg_sess'};var cb=typeof w._cg_consent!=='undefined'&&w._cg_consent===false;var p=new URLSearchParams(w.location.search);var t=p.get('_cgt');if(t&&!cb){d.cookie=C.cookieName+'='+encodeURIComponent(t)+';max-age='+(C.cookieDays*86400)+';path=/;SameSite=Lax;Secure';}var vt=null;if(!cb){var m=d.cookie.match(new RegExp('(?:^|;)\\\\s*'+C.cookieName+'=([^;]+)'));vt=m?decodeURIComponent(m[1]):null;}if(!vt&&t){vt=t;}var si=w.sessionStorage.getItem(C.sessionKey);if(!si){si=Date.now().toString(36)+Math.random().toString(36).substr(2,9);w.sessionStorage.setItem(C.sessionKey,si);}var ua=navigator.userAgent;var dv=/Mobi|Android/i.test(ua)?(/Tablet|iPad/i.test(ua)?'tablet':'mobile'):'desktop';var pl=JSON.stringify({site_key:C.siteKey,tracking_token:vt,session_id:si,page_url:w.location.origin+w.location.pathname,page_title:d.title||'',referrer:d.referrer||'',device_type:dv,timestamp:new Date().toISOString()});if(navigator.sendBeacon){navigator.sendBeacon(C.endpoint,new Blob([pl],{type:'text/plain'}));}else{var x=new XMLHttpRequest();x.open('POST',C.endpoint,true);x.setRequestHeader('Content-Type','text/plain');x.send(pl);}})(window,document);
</script>`;

const TrackedSiteSetup = ({
  sites,
  onAddSite,
  onToggleActive,
  onRemoveSite,
  onManageUsers,
  canManage,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [domain, setDomain] = useState('');
  const [siteName, setSiteName] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [copiedSnippetId, setCopiedSnippetId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!domain.trim() || !siteName.trim()) return;

    setAddLoading(true);
    setAddError(null);
    try {
      await onAddSite({ domain: domain.trim(), siteName: siteName.trim() });
      setDomain('');
      setSiteName('');
      setShowAddForm(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleCopySnippet = async (site) => {
    try {
      await navigator.clipboard.writeText(SNIPPET_TEMPLATE(site.snippet_key));
      setCopiedSnippetId(site.id);
      setTimeout(() => setCopiedSnippetId(null), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = SNIPPET_TEMPLATE(site.snippet_key);
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedSnippetId(site.id);
      setTimeout(() => setCopiedSnippetId(null), 2000);
    }
  };

  const handleToggle = async (siteId) => {
    setActionLoading(siteId);
    try {
      await onToggleActive(siteId);
    } catch (err) {
      console.error('Toggle error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (siteId) => {
    setActionLoading(siteId);
    try {
      await onRemoveSite(siteId);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tracked Sites</h3>
          <p className="text-sm text-gray-500 mt-1">
            Add your websites and install the tracking snippet to start monitoring visitors.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Site
          </button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
        >
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="otherwise.fi"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Enter without http:// or trailing slash</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                <input
                  type="text"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Otherwise Website"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  required
                />
              </div>
            </div>

            {addError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {addError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setAddError(null); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
              >
                {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Site
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Sites List */}
      {sites.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No tracked sites yet</p>
          <p className="text-gray-400 text-sm mt-1">Add your first website to start tracking visitors</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map(site => (
            <motion.div
              key={site.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`bg-white border rounded-xl p-5 shadow-sm ${
                site.is_active ? 'border-gray-200' : 'border-orange-200 bg-orange-50/30'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    site.is_active ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <Globe className={`w-5 h-5 ${site.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{site.site_name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sm text-gray-500">{site.domain}</span>
                      <a
                        href={`https://${site.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-500 hover:text-primary-700"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        site.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {site.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  </div>
                </div>

                {canManage && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopySnippet(site)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Copy tracking snippet"
                    >
                      {copiedSnippetId === site.id ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Code className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-600">Copy Snippet</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => onManageUsers(site.id)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Manage users"
                    >
                      <Users className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleToggle(site.id)}
                      disabled={actionLoading === site.id}
                      className={`p-2 rounded-lg transition-colors ${
                        site.is_active
                          ? 'text-orange-400 hover:text-orange-600 hover:bg-orange-50'
                          : 'text-green-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={site.is_active ? 'Pause tracking' : 'Resume tracking'}
                    >
                      {actionLoading === site.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : site.is_active ? (
                        <PowerOff className="w-4 h-4" />
                      ) : (
                        <Power className="w-4 h-4" />
                      )}
                    </button>

                    <button
                      onClick={() => setDeleteConfirm(site.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete site"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Snippet Key Display */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-gray-400">Snippet Key:</span>
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-600">
                  {site.snippet_key?.substring(0, 12)}...
                </code>
              </div>

              {/* Delete Confirmation */}
              {deleteConfirm === site.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-sm text-red-700">
                    Delete <strong>{site.site_name}</strong>? This will remove all visit data for this site.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleDelete(site.id)}
                      disabled={actionLoading === site.id}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === site.id ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Installation Guide */}
      {sites.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h4 className="font-medium text-blue-900 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Installation Guide
          </h4>
          <div className="mt-2 text-sm text-blue-700 space-y-1">
            <p>1. Click <strong>Copy Snippet</strong> next to your site above</p>
            <p>2. Paste the snippet before <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code> on every page</p>
            <p>3. Visitors who click tracked email links will be automatically identified</p>
          </div>
          <div className="mt-3">
            <details className="text-sm">
              <summary className="text-blue-600 cursor-pointer hover:text-blue-800 font-medium">
                Platform-specific instructions
              </summary>
              <div className="mt-2 space-y-1 text-blue-700">
                <p><strong>WordPress:</strong> Appearance &rarr; Theme Editor &rarr; footer.php, or use "Insert Headers & Footers" plugin</p>
                <p><strong>Webflow:</strong> Project Settings &rarr; Custom Code &rarr; Footer Code</p>
                <p><strong>Squarespace:</strong> Settings &rarr; Advanced &rarr; Code Injection &rarr; Footer</p>
                <p><strong>Shopify:</strong> Online Store &rarr; Themes &rarr; Edit Code &rarr; theme.liquid</p>
                <p><strong>Next.js/React:</strong> Add in _document.js or layout.tsx as a Script tag</p>
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackedSiteSetup;
